import React, { useEffect, useMemo, useState } from 'react';
import { 
  ChartBarIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  CurrencyDollarIcon 
} from '@heroicons/react/24/outline';
import { useLocation } from '../contexts/LocationContext';
import { usePonds } from '../hooks/useApi';
import { Link } from 'react-router-dom';
import apiClient from '../utils/api';

const ReportCard: React.FC<{
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
}> = ({ title, description, icon: Icon, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200">
    <div className="flex items-center space-x-4">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>
      <DocumentArrowDownIcon className="h-5 w-5 text-gray-400" />
    </div>
  </div>
);

const Reports: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [selectedPondId, setSelectedPondId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [complianceRows, setComplianceRows] = useState<any[]>([]);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const pondOptions = useMemo(() => ponds || [], [ponds]);

  useEffect(() => {
    // default range: last 7 days
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const from = new Date(today.getTime() - 6 * 86400000).toISOString().slice(0, 10);
    setStart(from);
    setEnd(to);
  }, []);

  const fetchCompliance = async () => {
    if (!selectedLocation || !start || !end) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/reports/compliance', {
        params: {
          location_id: selectedLocation.id,
          start,
          end,
          pond_id: selectedPondId || undefined,
        },
      });
      setComplianceRows(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load compliance report');
      setComplianceRows([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadComplianceCsv = async () => {
    if (!selectedLocation || !start || !end) return;
    try {
      const res = await apiClient.get(`/reports/export/compliance`, {
        params: {
          location_id: selectedLocation.id,
          start,
          end,
          pond_id: selectedPondId || undefined,
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_${start}_${end}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // ignore for now
    }
  };

  const reports = [
    {
      title: 'Daily Compliance Report',
      description: 'Missing feed, medicine, and water quality logs',
      icon: CalendarIcon,
      color: 'bg-blue-600',
      to: '/reports/compliance',
    },
    {
      title: 'Feed & Medicine Usage',
      description: 'Trends and pond-wise breakdown over time',
      icon: ChartBarIcon,
      color: 'bg-green-600',
      to: '/reports/feed-trends',
    },
    {
      title: 'Medicine Usage Trends',
      description: 'Daily totals and per-type breakdown',
      icon: ChartBarIcon,
      color: 'bg-purple-600',
      to: '/reports/medicine-trends',
    },
    {
      title: 'Mortality Report',
      description: 'Daily mortality (kg) and counts',
      icon: ChartBarIcon,
      color: 'bg-red-600',
      to: '/reports/mortality',
    },
    {
      title: 'Inventory & Growth Report',
      description: 'Fish count, biomass, and average size per pond',
      icon: ChartBarIcon,
      color: 'bg-indigo-600',
      to: '/reports/inventory-snapshot',
    },
    {
      title: 'Cycle Count',
      description: 'Manual reconciliation for pond values',
      icon: ChartBarIcon,
      color: 'bg-orange-600',
      to: '/reports/cycle-count',
    },
    {
      title: 'Financial Reports',
      description: 'Expenses vs Income with profit/loss analysis',
      icon: CurrencyDollarIcon,
      color: 'bg-orange-600',
      to: '/reports/financials',
    },
    {
      title: 'Revenue Projections',
      description: 'Projected revenue based on current biomass',
      icon: ChartBarIcon,
      color: 'bg-green-600',
      to: '/reports/revenue-projections',
    },
    {
      title: 'Cost Analysis',
      description: 'Detailed cost breakdown and feed efficiency',
      icon: ChartBarIcon,
      color: 'bg-blue-600',
      to: '/reports/cost-analysis',
    },
    {
      title: 'Cycle Planning Report',
      description: 'Stocking to harvest timeline and forecasts',
      icon: ChartBarIcon,
      color: 'bg-cyan-600',
    },
  ];

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <ChartBarIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Reports & Analytics</h1>
        <p className="text-lg text-gray-600">
          Generate detailed reports and export data
        </p>
      </div>


      {/* Reports List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Available Reports</h2>
        {reports.map((report, index) => (
          <Link key={index} to={report.to || '#'}>
            <ReportCard
              title={report.title}
              description={report.description}
              icon={report.icon}
              color={report.color}
            />
          </Link>
        ))}
      </div>

      {/* Export Options */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Export Options</h2>
        <div className="flex flex-wrap gap-3">
          <button className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export to Excel
          </button>
          <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export to CSV
          </button>
          <button className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export to PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reports;